import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import icon from 'leaflet/dist/images/marker-icon.png';
import shadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { Button, Input, Typography } from 'glintly-ui';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { parseGoogleMapsUrl } from '../utils/parseGoogleMapsUrl';
import { searchPlacesPhoton, reverseGeocodePhoton } from '../utils/geocodeSearch';
import styles from './LocationPickerMap.module.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: shadow,
});

const DEFAULT_CENTER = [19.076, 72.8777];
const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_CHARS = 2;

function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapRecenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
    map.setView([lat, lng], Math.max(map.getZoom(), 15));
  }, [lat, lng, map]);
  return null;
}

function MapSizeFix() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [map]);
  return null;
}

/**
 * @param {(payload: { lat: number, lng: number, cityHintFromUrl?: string, cityNameFromPhoton?: string }) => void} props.onChosen
 */
export default function LocationPickerMap({ onChosen, biasLat, biasLng }) {
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchError, setMapSearchError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [mapsLink, setMapsLink] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkApplying, setLinkApplying] = useState(false);
  const searchRequestId = useRef(0);
  const searchDebounceRef = useRef(null);

  const latNum = lat === '' ? null : Number(lat);
  const lngNum = lng === '' ? null : Number(lng);
  const markerValid =
    latNum != null && lngNum != null && !Number.isNaN(latNum) && !Number.isNaN(lngNum);

  const center = markerValid ? [latNum, lngNum] : DEFAULT_CENTER;

  const bias = useMemo(() => {
    if (markerValid && latNum != null && lngNum != null) {
      return { lat: latNum, lon: lngNum };
    }
    if (
      biasLat != null &&
      biasLng != null &&
      !Number.isNaN(Number(biasLat)) &&
      !Number.isNaN(Number(biasLng))
    ) {
      return { lat: Number(biasLat), lon: Number(biasLng) };
    }
    return { lat: DEFAULT_CENTER[0], lon: DEFAULT_CENTER[1] };
  }, [markerValid, latNum, lngNum, biasLat, biasLng]);

  const emitChosen = useCallback(
    (la, ln, opts = {}) => {
      const roundedLa = Math.round(la * 1e6) / 1e6;
      const roundedLn = Math.round(ln * 1e6) / 1e6;
      onChosen({
        lat: roundedLa,
        lng: roundedLn,
        cityHintFromUrl: opts.cityHintFromUrl,
        cityNameFromPhoton: opts.cityNameFromPhoton,
      });
    },
    [onChosen]
  );

  const handlePick = async (latitude, longitude) => {
    const la = Math.round(latitude * 1e6) / 1e6;
    const ln = Math.round(longitude * 1e6) / 1e6;
    setLat(String(la));
    setLng(String(ln));
    setLinkError('');
    setMapSearchError('');
    try {
      const rev = await reverseGeocodePhoton(la, ln);
      if (rev?.cityName) {
        emitChosen(la, ln, { cityNameFromPhoton: rev.cityName });
        toast.success('Location set from map.');
      } else {
        emitChosen(la, ln, {});
        toast.success('Pin saved — using coordinates for distances.');
      }
    } catch {
      emitChosen(la, ln, {});
      toast.info('Pin saved.');
    }
  };

  const runSearchForQuery = useCallback(
    async (rawQuery) => {
      const q = rawQuery.trim();
      if (!q) {
        setSearchResults([]);
        setMapSearchError('');
        setMapSearchLoading(false);
        return;
      }
      if (q.length < MIN_SEARCH_CHARS) {
        setSearchResults([]);
        setMapSearchError('');
        setMapSearchLoading(false);
        return;
      }

      const reqId = ++searchRequestId.current;
      setMapSearchError('');
      setMapSearchLoading(true);
      try {
        const results = await searchPlacesPhoton(q, bias);
        if (reqId !== searchRequestId.current) return;
        setSearchResults(results);
        if (results.length === 0) {
          setMapSearchError('No places found. Try another spelling or tap the map.');
        }
      } catch {
        if (reqId !== searchRequestId.current) return;
        setMapSearchError('Search failed. Check your connection.');
      } finally {
        if (reqId === searchRequestId.current) {
          setMapSearchLoading(false);
        }
      }
    },
    [bias]
  );

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      void runSearchForQuery(mapSearchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [mapSearchQuery, runSearchForQuery]);

  const pickSearchResult = (r) => {
    setSearchResults([]);
    setMapSearchError('');
    setLinkError('');
    setLat(String(Math.round(r.lat * 1e6) / 1e6));
    setLng(String(Math.round(r.lng * 1e6) / 1e6));
    const photonCity = r.cityName ? String(r.cityName).trim() : '';
    emitChosen(r.lat, r.lng, {
      cityNameFromPhoton: photonCity || undefined,
    });
    toast.success('Location set from search.');
  };

  const applyMapsLink = async () => {
    setLinkError('');
    const parsed = parseGoogleMapsUrl(mapsLink);
    if (!parsed) {
      setLinkError(
        'Paste a maps URL with coordinates, /place/…, or ?q=address. Short goo.gl links may not work — copy the long URL from the browser.'
      );
      return;
    }

    if (parsed.placeQuery) {
      setLinkApplying(true);
      try {
        const results = await searchPlacesPhoton(parsed.placeQuery.trim(), bias);
        if (results.length === 0) {
          setLinkError('No place found for this link.');
          return;
        }
        const r = results[0];
        emitChosen(r.lat, r.lng, {
          cityHintFromUrl: parsed.placePathCity,
          cityNameFromPhoton: r.cityName || undefined,
        });
        toast.success('Location set from link.');
      } catch {
        setLinkError('Could not use this link.');
      } finally {
        setLinkApplying(false);
      }
      return;
    }

    const { lat: pLat, lng: pLng, placePathCity } = parsed;
    setLinkApplying(true);
    try {
      const rev = await reverseGeocodePhoton(pLat, pLng);
      if (rev) {
        emitChosen(pLat, pLng, {
          cityHintFromUrl: placePathCity,
          cityNameFromPhoton: rev.cityName || undefined,
        });
        toast.success('Location set from link.');
      } else if (placePathCity) {
        emitChosen(pLat, pLng, { cityHintFromUrl: placePathCity });
        toast.success('Location set from link.');
      } else {
        emitChosen(pLat, pLng, {});
        toast.success('Coordinates saved from link.');
      }
    } catch {
      emitChosen(pLat, pLng, {});
      toast.info('Pin saved from link.');
    } finally {
      setLinkApplying(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <Typography variant="body2" className={styles.sectionTitle}>
        Search, map, or paste a Google Maps link
      </Typography>

      <div className={styles.searchRow}>
        <div className={styles.searchInput}>
          <Input.Root name="loc-search">
            <Input.Control
              value={mapSearchQuery}
              onChange={(e) => {
                setMapSearchQuery(e.target.value);
                setMapSearchError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (searchDebounceRef.current) {
                    clearTimeout(searchDebounceRef.current);
                    searchDebounceRef.current = null;
                  }
                  void runSearchForQuery(mapSearchQuery);
                }
              }}
              placeholder="Start typing a place or address…"
            />
          </Input.Root>
        </div>
        <div className={styles.searchStatus} aria-live="polite" aria-busy={mapSearchLoading}>
          {mapSearchLoading ? (
            <Loader2 className={styles.searchSpinner} size={22} aria-label="Searching" />
          ) : null}
        </div>
      </div>
      {mapSearchError ? <p className={styles.errorText}>{mapSearchError}</p> : null}
      {searchResults.length > 0 ? (
        <ul className={styles.resultList} role="listbox">
          {searchResults.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className={styles.resultBtn}
                onClick={() => pickSearchResult(r)}
              >
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.mapWrap}>
        <MapContainer
          center={center}
          zoom={markerValid ? 15 : 12}
          className={styles.map}
          scrollWheelZoom
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapSizeFix />
          <MapRecenter lat={markerValid ? latNum : null} lng={markerValid ? lngNum : null} />
          <MapClickHandler onPick={handlePick} />
          {markerValid ? <Marker position={[latNum, lngNum]} /> : null}
        </MapContainer>
      </div>
      <p className={styles.hint}>Tap the map to drop a pin and set your area.</p>

      <div className={styles.linkBlock}>
        <Input.Root name="maps-link">
          <Input.Label>Paste Google Maps link (optional)</Input.Label>
          <Input.Control
            type="textarea"
            rows={2}
            value={mapsLink}
            onChange={(e) => {
              setMapsLink(e.target.value);
              setLinkError('');
            }}
            placeholder="Open Google Maps → Share → Copy link"
          />
        </Input.Root>
        {linkError ? <p className={styles.errorText}>{linkError}</p> : null}
        <div className={styles.applyRow}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyMapsLink}
            disabled={linkApplying}
          >
            {linkApplying ? 'Applying…' : 'Use link'}
          </Button>
        </div>
      </div>
    </div>
  );
}
